<main style="flex-grow: 1;">
  <section 
    class="spirits-section section-padding" 
    aria-labelledby="spirits-heading"
    itemscope 
    itemtype="https://schema.org/ItemList"
  >
    <meta itemprop="name" content="Vitrinas de Espíritus Artesanales Estancos Distilling Co." />
    <meta itemprop="description" content="Vitrinas exclusivas de destilados de alta gama madurados a 2500 msnm en los Andes ecuatorianos." />
    <meta itemprop="numberOfItems" [content]="spirits().length" />

    <div class="container">

      <header class="section-header">
        <span class="section-subtitle">Vitrinas de Selección Privada</span>
        <h2 id="spirits-heading">Vitrinas de Espíritus de Altura</h2>
        <div class="header-divider" aria-hidden="true">
          <span class="divider-line"></span>
          <span class="divider-diamond">◆</span>
          <span class="divider-line"></span>
        </div>
        <p class="section-description-aeo">
          Explora directamente nuestras vitrinas de licores artesanales. Entra a la experiencia dedicada de cada destilado para conocer su historia, notas de cata y origen a 2500 msnm.
        </p>
      </header>

      <!-- ESTADO DE CARGA -->
      @if (isLoading()) {
        <div class="loader-container" role="status" aria-live="polite">
          <div class="spinner" aria-hidden="true"></div>
          <div class="loader-text">Abriendo Vitrinas de Espíritus...</div>
        </div>
      } @else {
        <!-- GRID DE VITRINAS -->
        <div class="vitrinas-grid">
          @for (spirit of spirits(); track spirit.id; let i = $index) {
            <a 
              [routerLink]="getSpiritRoute(spirit.name)"
              class="vitrina-card"
              itemprop="itemListElement" 
              itemscope 
              itemtype="https://schema.org/Product"
              [attr.aria-label]="'Entrar a la vitrina de ' + spirit.name"
            >
              <meta itemprop="position" [content]="i + 1" />
              <meta itemprop="url" [content]="getSpiritRoute(spirit.name)" />

              <!-- ESTRUCTURA DE LA VITRINA DE CRISTAL E ILUMINACIÓN -->
              <div class="vitrina-showcase-box">
                <div class="vitrina-top-badge">
                  <span class="vitrina-num">Vitrina N° {{ i === 0 ? 'I' : i === 1 ? 'II' : 'III' }}</span>
                  <span class="vitrina-direct-tag">Entrar Directo</span>
                </div>

                <!-- LUZ RADIAL DEL PEDESTAL -->
                <div class="vitrina-light-halo"></div>

                <!-- OVERLAY FLOTANTE EN VITRINA -->
                <div class="vitrina-glass-overlay">
                  <span class="glass-click-hint">Ver Notas de Cata &rarr;</span>
                </div>

                <div class="vitrina-image-container">
                  <img
                    [src]="getOptimizedImageUrl(spirit.image_url, 500, 85)"
                    [alt]="'Vitrina de ' + spirit.name + ' - Licor artesanal de altura'"
                    class="vitrina-bottle-img"
                    [attr.loading]="i === 0 ? 'eager' : 'lazy'"
                    [attr.fetchpriority]="i === 0 ? 'high' : 'auto'"
                    decoding="async"
                    width="350"
                    height="450"
                    itemprop="image"
                    (error)="onImageError($event, spirit.name)"
                  >
                </div>
              </div>

              <!-- DATOS Y ESPECIFICACIONES EN LA VITRINA -->
              <div class="vitrina-info">
                <span class="vitrina-category" itemprop="category">{{ spirit.category }}</span>
                <h3 class="vitrina-title" itemprop="name">{{ spirit.name }}</h3>

                <!-- CALIFICACIÓN DE 5 ESTRELLAS -->
                <div class="vitrina-rating-badge" [attr.aria-label]="'Calificación ' + getSpiritRating(spirit).average + ' de 5 estrellas'">
                  <div class="stars-gold">
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                  </div>
                  <span class="rating-score">{{ getSpiritRating(spirit).average | number:'1.1-1' }}</span>
                  <span class="rating-count">({{ getSpiritRating(spirit).count }} reseñas)</span>
                </div>

                <!-- MINI ESPECIFICACIONES DE CATA -->
                <div class="vitrina-specs-row">
                  <span class="vitrina-spec-pill">{{ getSpiritAbv(spirit) }}</span>
                  <span class="vitrina-spec-pill">{{ getSpiritAge(spirit) }}</span>
                  <span class="vitrina-spec-pill">2500 MSNM</span>
                </div>

                <!-- DESCRIPCIÓN CORTA -->
                <p class="vitrina-short-desc" itemprop="description">
                  {{ getShortDescription(spirit) }}
                </p>

                <!-- MICRODATOS OCULTOS SCHEMA.ORG -->
                <div itemprop="brand" itemscope itemtype="https://schema.org/Brand" style="display:none;">
                  <meta itemprop="name" content="Estancos Distilling Co." />
                </div>
                <div itemprop="aggregateRating" itemscope itemtype="https://schema.org/AggregateRating" style="display:none;">
                  <meta itemprop="ratingValue" [content]="getSpiritRating(spirit).average" />
                  <meta itemprop="reviewCount" [content]="getSpiritRating(spirit).count" />
                  <meta itemprop="bestRating" content="5" />
                  <meta itemprop="worstRating" content="1" />
                </div>
                @if (spirit.price) {
                  <div itemprop="offers" itemscope itemtype="https://schema.org/Offer" style="display:none;">
                    <meta itemprop="price" [content]="spirit.price" />
                    <meta itemprop="priceCurrency" content="USD" />
                    <meta itemprop="availability" content="https://schema.org/InStock" />
                    <meta itemprop="seller" content="Estancos Distilling Co." />

                    <!-- POLÍTICA DE ENVÍOS (SHIPPING DETAILS - FICHA COMERCIANTE GOOGLE) -->
                    <div itemprop="shippingDetails" itemscope itemtype="https://schema.org/OfferShippingDetails">
                      <div itemprop="shippingRate" itemscope itemtype="https://schema.org/MonetaryAmount">
                        <meta itemprop="value" content="5.00" />
                        <meta itemprop="currency" content="USD" />
                      </div>
                      <div itemprop="shippingDestination" itemscope itemtype="https://schema.org/DefinedRegion">
                        <meta itemprop="addressCountry" content="EC" />
                      </div>
                      <div itemprop="deliveryTime" itemscope itemtype="https://schema.org/ShippingDeliveryTime">
                        <div itemprop="handlingTime" itemscope itemtype="https://schema.org/QuantitativeValue">
                          <meta itemprop="minValue" content="1" />
                          <meta itemprop="maxValue" content="5" />
                          <meta itemprop="unitCode" content="d" />
                        </div>
                        <div itemprop="transitTime" itemscope itemtype="https://schema.org/QuantitativeValue">
                          <meta itemprop="minValue" content="1" />
                          <meta itemprop="maxValue" content="4" />
                          <meta itemprop="unitCode" content="d" />
                        </div>
                      </div>
                    </div>

                    <!-- POLÍTICA DE DEVOLUCIONES (MERCHANT RETURN POLICY - FICHA COMERCIANTE GOOGLE) -->
                    <div itemprop="hasMerchantReturnPolicy" itemscope itemtype="https://schema.org/MerchantReturnPolicy">
                      <meta itemprop="applicableCountry" content="EC" />
                      <meta itemprop="returnPolicyCategory" content="https://schema.org/MerchantReturnFiniteReturnWindow" />
                      <meta itemprop="merchantReturnDays" content="15" />
                      <meta itemprop="returnMethod" content="https://schema.org/ReturnByMail" />
                      <meta itemprop="returnFees" content="https://schema.org/FreeReturn" />
                    </div>
                  </div>
                }

                <!-- BOTÓN PRINCIPAL DE ENTRADA A LA VITRINA -->
                <div class="vitrina-action-btn">
                  <span>Entrar a la Vitrina</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="arrow-icon" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>

              </div>

            </a>
          }
        </div>

        <!-- BOTÓN FOOTER PARA VER EL CATÁLOGO COMPLETO -->
        <div class="spirits-catalog-footer">
          <a routerLink="/espiritus" class="btn-catalog-view-all">
            <span>Ver Catálogo Completo de Espíritus</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </a>
        </div>
      }

    </div>
  </section>
</main>

